import { useEffect, useId, useMemo, useState } from 'react';
import { Check, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface VisitTimePayload {
  startedTime: string;
  endedTime: string;
  timezoneOffsetMinutes: number;
}

export interface VisitTimeEditorDraft extends VisitTimePayload {
  expanded: boolean;
  isValid: boolean;
  durationMinutes: number | null;
}

interface VisitTimeEditorProps {
  startedAt: string | null | undefined;
  endedAt?: string | null | undefined;
  className?: string;
  label?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  onDraftChange?: (draft: VisitTimeEditorDraft) => void;
  onSave?: (payload: VisitTimePayload) => void;
  isSaving?: boolean;
  disabled?: boolean;
  saveLabel?: string;
}

interface ParsedTime {
  hours: number;
  minutes: number;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTimeInput(date: Date | null): string {
  if (!date) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function parseTimeInput(value: string): ParsedTime | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

function applyTime(baseDate: Date, time: ParsedTime): Date {
  const date = new Date(baseDate);
  date.setHours(time.hours, time.minutes, 0, 0);
  return date;
}

function isSameLocalDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours} ч ${mins} мин`;
  if (hours > 0) return `${hours} ч`;
  return `${mins} мин`;
}

function resolveDuration(
  startDate: Date | null,
  endBaseDate: Date | null,
  startedTime: string,
  endedTime: string,
  now: Date,
): { durationMinutes: number | null; error: string | null } {
  if (!startDate || !endBaseDate) return { durationMinutes: null, error: 'Нет времени визита' };

  const parsedStart = parseTimeInput(startedTime);
  const parsedEnd = parseTimeInput(endedTime);
  if (!parsedStart || !parsedEnd) return { durationMinutes: null, error: 'Проверьте время' };

  const startedAt = applyTime(startDate, parsedStart);
  const endedAt = applyTime(endBaseDate, parsedEnd);

  if (startedAt > endedAt) return { durationMinutes: null, error: 'Выход раньше входа' };
  if (endedAt > now && !isSameLocalDate(endedAt, now)) {
    return { durationMinutes: null, error: 'Выход в другой день' };
  }

  const durationMinutes = Math.round((endedAt.getTime() - startedAt.getTime()) / 60000);
  if (durationMinutes > 24 * 60) return { durationMinutes: null, error: 'Больше 24 часов' };

  return { durationMinutes, error: null };
}

export function VisitTimeEditor({
  startedAt,
  endedAt,
  className,
  label = 'Будет сохранено',
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  onDraftChange,
  onSave,
  isSaving = false,
  disabled = false,
  saveLabel = 'Сохранить',
}: VisitTimeEditorProps) {
  const inputId = useId();
  const [now, setNow] = useState(() => new Date());
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = expanded ?? internalExpanded;

  const startDate = useMemo(() => toDate(startedAt), [startedAt]);
  const storedEndDate = useMemo(() => toDate(endedAt), [endedAt]);
  const endBaseDate = storedEndDate ?? now;
  const defaultStartTime = useMemo(() => formatTimeInput(startDate), [startDate]);
  const defaultEndTime = useMemo(() => formatTimeInput(endBaseDate), [endBaseDate]);
  const [startedTime, setStartedTime] = useState(defaultStartTime);
  const [endedTime, setEndedTime] = useState(defaultEndTime);
  const timezoneOffsetMinutes = endBaseDate.getTimezoneOffset();

  useEffect(() => {
    if (storedEndDate) return;
    const interval = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, [storedEndDate]);

  useEffect(() => {
    if (isExpanded) return;
    setStartedTime(defaultStartTime);
    setEndedTime(defaultEndTime);
  }, [defaultStartTime, defaultEndTime, isExpanded]);

  useEffect(() => {
    setInternalExpanded(defaultExpanded);
  }, [startedAt, endedAt, defaultExpanded]);

  const validation = resolveDuration(startDate, endBaseDate, startedTime, endedTime, now);
  const isValid = !validation.error;

  useEffect(() => {
    onDraftChange?.({
      startedTime,
      endedTime,
      timezoneOffsetMinutes,
      expanded: isExpanded,
      isValid,
      durationMinutes: validation.durationMinutes,
    });
  }, [
    endedTime,
    isExpanded,
    isValid,
    onDraftChange,
    startedTime,
    timezoneOffsetMinutes,
    validation.durationMinutes,
  ]);

  if (!startDate) return null;

  const setExpanded = (value: boolean) => {
    if (onExpandedChange) {
      onExpandedChange(value);
    } else {
      setInternalExpanded(value);
    }
  };

  const canSave = isValid && !disabled && !isSaving;

  return (
    <div
      data-testid="visit-time-editor"
      className={cn('rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {label}: {startedTime || '--:--'} &rarr; {endedTime || '--:--'}
            {validation.durationMinutes !== null && ` · ${formatDuration(validation.durationMinutes)}`}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 px-2 text-xs"
          onClick={() => setExpanded(!isExpanded)}
          disabled={disabled || isSaving}
        >
          {isExpanded ? 'Скрыть' : 'Изменить'}
        </Button>
      </div>

      {isExpanded && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2">
            <div className="min-w-0 space-y-1">
              <Label htmlFor={`${inputId}-start`} className="text-xs text-muted-foreground">
                Вход
              </Label>
              <Input
                id={`${inputId}-start`}
                type="time"
                value={startedTime}
                onChange={(event) => setStartedTime(event.target.value)}
                disabled={disabled || isSaving}
                className="h-9 min-w-0 px-2 text-center text-sm"
              />
            </div>
            <div className="min-w-0 space-y-1">
              <Label htmlFor={`${inputId}-end`} className="text-xs text-muted-foreground">
                Выход
              </Label>
              <Input
                id={`${inputId}-end`}
                type="time"
                value={endedTime}
                onChange={(event) => setEndedTime(event.target.value)}
                disabled={disabled || isSaving}
                className="h-9 min-w-0 px-2 text-center text-sm"
              />
            </div>
          </div>

          {validation.error && (
            <div className="text-xs text-destructive">{validation.error}</div>
          )}

          {onSave && (
            <Button
              type="button"
              size="sm"
              className="h-8 w-full"
              onClick={() => onSave({ startedTime, endedTime, timezoneOffsetMinutes })}
              disabled={!canSave}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {saveLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
